import z from "zod";
import {
  CommentSchema,
  GetGroupedOpportunitiesDataResponseSchema,
  GetOpportunitiesDataResponseSchema,
  GetQuerySQLLineageResponseSchema,
  LabelSuggestionResponseSchema,
  OpportunityDetailResponseSchema,
  PriorityOpportunityResponseSchema,
  QueryLineageTagReportSchema,
  QueryLineageTagSchema,
  QueryTagMetaSchema,
} from "./schemas";

// Re-export schemas and schema-derived types
export {
  FilterResponseSchema,
  MoneySavingsSchema,
  ResourceSchema,
  SavingsAttributionSchema,
  GetOpportunitiesDataResponseSchema,
  GetGroupedOpportunitiesDataResponseSchema,
  GetOpportunitiesSummaryResponseSchema,
  GetOpportunitiesResourceResponseSchema,
  GetOpportunitiesRedirectResponseSchema,
  GetOpportunityUpdatesResponseSchema,
  GetScheduledEventUpdatesResponseSchema,
  GetQuerySQLLineageResponseSchema,
  OpportunitiesFiltersSchema,
  GroupedOpportunitiesFiltersSchema,
  PriorityOpportunityResponseSchema,
  CommentSchema,
  OpportunityDetailResponseSchema,
  LabelSuggestionResponseSchema,
} from "./schemas";

export type {
  OpportunitiesFilters,
  GroupedOpportunitiesFilters,
  GetOpportunityUpdatesResponse,
  GetScheduledEventUpdatesResponse,
} from "./schemas";

export enum Effort {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  ZERO = "zero",
}

export enum Status {
  ACTIVE = "active",
  IMPLEMENTED = "implemented",
  DISMISSED = "dismissed",
}

export enum Period {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
}

// Types for individual components within the responses
export interface Resource {
  resource_name: string;
  resource_uri: string;
  resource_type: number;
}

export interface GetOpportunitiesRedirectionRequest {
  resource_type: number | null;
  resource_uri: string | null;
  resource_id: string | null;
  resource_name: string | null;
  instance_id: string;
  opportunity_id?: string | null;
}

export interface GetOpportunitiesResourceRequest {
  resource_uri: string;
  resource_type: ResourceType;
  instance_id: string;
  start_date: string;
  end_date: string;
  status?: string[];
  sort?: string;
  sort_order?: string;
}

export interface GetScheduledEventsOpportunityRequest {
  warehouse_name: string;
  start_date: string;
  end_date: string;
}

export interface GetOpportunityUpdatesRequest {
  opportunity_id: string;
}

export interface GetScheduledEventUpdatesRequest {
  schedule_id: string;
}

export interface UpdateOpportunitiesDataRequest {
  changes: Array<{
    opportunity_id: string;
    status: string | null | undefined;
    owner: string | null | undefined;
  }>;
  user_id: number | undefined;
  start_date: string;
  end_date: string;
}

export interface GetOpportunitiesSummaryRequest {
  start_date: string;
  end_date?: string;
  effort?: string[] | null;
  resource_name?: string[] | null;
  opportunity_name?: string[] | null;
  resource_type?: string[] | null;
  owner?: string[] | null;
  status?: string[] | null;
  user?: string[] | null;
  warehouse?: string[] | null;
  instance_id?: string[] | null;
  // Databricks workspace filter (More-Filters chip). Mirrors the field on
  // `OpportunitiesFiltersSchema` in `schemas.ts` and `GetOpportunitiesSummaryRequest`
  // on the BE — without this declaration the type was stale and runtime
  // payloads slipped through via structural typing.
  workspace_id?: string[] | null;
}

export interface MoneySavings {
  min: string;
  max: string;
}

// Response types
export type GetOpportunitiesDataResponse = z.infer<typeof GetOpportunitiesDataResponseSchema>;

export type PriorityOpportunityResponse = z.infer<typeof PriorityOpportunityResponseSchema>;

export type OpportunityComment = z.infer<typeof CommentSchema>;

export type OpportunityDetailResponse = z.infer<typeof OpportunityDetailResponseSchema>;

export type LabelSuggestionResponse = z.infer<typeof LabelSuggestionResponseSchema>;

export interface SetLabelsRequest {
  opportunity_id: string;
  instance_id: number;
  labels: string[];
}

export interface AddCommentRequest {
  opportunity_id: string;
  instance_id: number;
  comment: string;
}

export interface UpdateCommentRequest {
  comment: string;
}

export type GetGroupedOpportunitiesDataResponse = z.infer<
  typeof GetGroupedOpportunitiesDataResponseSchema
>;

export interface GetOpportunitiesResourceResponse {
  data: Array<GetOpportunitiesDataResponse>;
}

export interface GetOpportunitiesRedirectResponse {
  url: string;
}

export interface UpdateOpportunitiesDataResponse {
  ok: boolean;
}

export interface GetOpportunitiesSummaryResponse {
  label: string;
  value: string;
}

export interface FilterResponse {
  label: string;
  value: string;
}

// Pagination response type for getOpportunities API
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
  size: number;
}

export interface GetOpportunitiesResourceNameFilterRequest {
  prefix?: string;
  page?: number;
  // Databricks workspace scoping for typeahead candidates. Sent as
  // repeated `?workspace_id=...` query params; the BE applies it as an
  // additional row-level predicate alongside RBAC. Undefined / empty
  // → unscoped (the chip isn't set).
  workspace_id?: string[];
}

export interface GetOpportunitiesUserFilterRequest {
  prefix?: string;
  page?: number;
  workspace_id?: string[];
}

export interface GetOpportunitiesWarehouseFilterRequest {
  prefix?: string;
  page?: number;
  workspace_id?: string[];
}

export type QueryLineageTagReport = z.infer<typeof QueryLineageTagReportSchema>;

export type QueryLineageTag = z.infer<typeof QueryLineageTagSchema>;

export type QueryTagMeta = z.infer<typeof QueryTagMetaSchema>;

export type GetQuerySQLLineageResponse = z.infer<typeof GetQuerySQLLineageResponseSchema>;

export enum ResourceType {
  QueryHash = 1,
  Table,
  Warehouse,
  User,
  Role,
  Dashboard = 13,
  DbtModel = 14,
  DbtProject = 15,
  Job = 17,
  SQLWarehouse = 18,
}

// Types for row selection and bulk actions
export interface SelectedRow {
  opportunity_id: string;
}

export interface OwnerListItem {
  opportunity_id: string;
  owner: number | string;
}
